import React, { useState, useRef, useEffect } from 'react'

import Form from 'components/form/form'
import copy from 'fast-copy'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'

import {
    getCurrentUser,
    getCurrentAccount,
} from 'state/selectors'

import { injectStripe } from 'react-stripe-elements'

import {
    getPlanAndCurrencyInfo,
    saveBillingDetails,
    validateCoupon,
    updateUserSubscriber
} from 'state/actions'

import yup from 'yup'
import { logAnalyticsEvent, logPixelEvent } from 'utils/analytics'

const Billing = ({
    setStep,
    userData,
    accountData,
    saveBillingDetails,
    refreshSlider,
    stripe,
    history,
    isActiveStep,
    getPlanAndCurrencyInfo,
    planAndCurrencyData,
    validateCoupon,
    updateUserSubscriber,
    isSecondaryAccount,
    tempCreatedAccount
}) => {
    const accountDataRef = useRef(isSecondaryAccount ? tempCreatedAccount : accountData)
    accountDataRef.current = isSecondaryAccount ? tempCreatedAccount : accountData

    const [isLoading, setIsLoading] = useState(false)

    const [formValues, setFormValues] = useState({})
    const formValuesRef = useRef(formValues)
    formValuesRef.current = formValues

    const [formError, setFormError] = useState(null)

    useEffect(() => {
        getPlanAndCurrencyInfo()
    }, [])

    const formRows = [
        [
            {
                key: 'billingType',
                type: 'billingType',
                props: {
                    planAndCurrencyData,
                    discountMonths: formValues.discountMonths,
                    discountPercent: formValues.discountPercent,
                    extraStorage: formValues.extraStorage,
                }
            }
        ], [
            {
                key: 'accountName',
                placeholder: `Billing account name`,
                type: 'text',
            }
        ], [
            {
                placeholder: 'Organization address',
                key: 'address',
                type: 'address',
            }
        ], [
            {
                key: 'coupon_code',
                placeholder: 'Have a coupon code?',
                type: 'text'
            }
        ], [
            {
                key: 'billingDetails',
                type: 'cardInput',
            }
        ]
    ]

    const handleError = message => {
        setFormError(message)

        setTimeout(() => {
            refreshSlider()
        }, 350)

        setTimeout(() => {
            setFormError(null)

            setTimeout(() => {
                refreshSlider()
            }, 350)
        }, 3200)
    }

    const goToDashboard = _ => {
        const _accountData = accountDataRef.current
        history.push('/' + _accountData.slug)
    }

    const handleProceed = _ => {
        goToDashboard()

        setStep('next')
    }

    const handleSkip = e => {
        e.preventDefault()
        e.stopPropagation()

        logPixelEvent('Lead')

        handleSaveBillingDetails()
    }

    const handleSubmit = async e => {
        e.preventDefault()
        // setStep('next')

        const _formValues = formValuesRef.current

        const validateParams = {
            billing_company_name: yup.string().required(`We'll need a company name first.`),
            billing_address: yup.string().required(`We'll need an address first.`),
            billing_city: yup.string().required(`We'll need you to enter a city first.`),
            billing_state: yup.string().required(`We'll need you to enter a state first.`),
        }

        const submissionSchema = yup.object().shape(validateParams)

        if (!_formValues.address) {
            return handleError(`We need an address`)
        }

        const {
            accountName,
            address: addressObject,
            coupon_code,
            discountMonths,
            discountPercent,
            extraStorage
        } = _formValues

        const {
            address,
            city,
            state,
            zip,
            countryCode
        } = addressObject

        const submissionData = {
            billing_company_name: accountName,
            billing_address: address,
            billing_city: city,
            billing_state: state,
            billing_postcode: zip,
            billing_is_annual: _formValues.billingType === 'annually',
            billing_country: countryCode,
            coupon_code,
            discountMonths,
            discountPercent,
            extraStorage
        }

        // Validate input fields against schema
        try {
            await submissionSchema.validate({ ...submissionData })
        } catch (err) {
            return handleError(err.message)
        }

        setIsLoading(true)

        let token = null

        const createTokenResponse = await stripe.createToken({ name: submissionData.billing_company_name })

        token = createTokenResponse.token

        if (!token || !token.id) {
            setIsLoading(false)

            return handleError("There seems to be an issue with your payment details.")
        }

        logAnalyticsEvent({
            category: 'Registration',
            action: 'Completed Step',
            label: '3 - Billing Details'
        })

        updateUserSubscriber({
            AddedPaymentDetails: 'Yes'
        })

        logPixelEvent('Purchase')

        handleSaveBillingDetails(submissionData, token)
    }

    const handleSaveBillingDetails = (submissionData = {}, token = null) => {
        const _accountData = accountDataRef.current

        if (!_accountData || !_accountData.id) {
            return alert('no account data')
        }

        saveBillingDetails({
            ...submissionData,
            user: userData,
            token_id: token ? token.id : null
        }, _accountData.id, error => {
            if (error) {
                setIsLoading(false)

                if (error.response && error.response.data) {
                    return handleError(error.response.data.message)
                } else {
                    return handleError("An unknown error has occurred.")
                }
            } else {
                handleProceed()
            }
        })
    }

    const handleEnterCoupon = code => {
        if (code.length >= 5) {
            validateCoupon(code, (response, testedCode) => {
                const _formValues = copy(formValuesRef.current)

                if (testedCode === code) {
                    if (response) {
                        _formValues.discountPercent = response.percent_off
                        _formValues.discountMonths = response.duration_in_months ? response.duration_in_months : 0

                        if(response.metadata && response.metadata.STORAGE_ADD){
                            _formValues.extraStorage = response.metadata.STORAGE_ADD
                        }
                    } else {
                        _formValues.discountPercent = null

                        handleError("Your coupon code seems invalid")
                    }

                    setFormValues(_formValues)
                }
            })
        } else {
            const _formValues = copy(formValuesRef.current)

            if (_formValues.discountPercent) {
                _formValues.discountPercent = null
                _formValues.discountMonths = 0

                setFormValues(_formValues)
            }
        }

    }

    const handleChange = (key, value) => {
        const _formValues = copy(formValues)

        _formValues[key] = value

        setFormValues(_formValues)

        if (key === 'coupon_code') {
            handleEnterCoupon(value)
        }
    }

    return (
        <React.Fragment>
            <Form
                rows={formRows}
                values={formValues}
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                isLoading={isLoading}
                error={formError}
                isFocused={isActiveStep}
                buttonPrimary={{
                    label: 'Save details',
                    icon: 'tick'
                }}
                buttonLow={{
                    label: `Skip for now`,
                    callback: handleSkip
                }}
            />
        </React.Fragment>
    )
}

const mapStateToProps = state => {
    return {
        userData: getCurrentUser(state, true),
        accountData: getCurrentAccount(state),
        planAndCurrencyData: state.auth.planAndCurrencyData,
        tempCreatedAccount: state.account.tempCreatedAccount
    }
}

export default injectStripe(withRouter(connect(mapStateToProps, {
    saveBillingDetails,
    getPlanAndCurrencyInfo,
    validateCoupon,
    updateUserSubscriber
})(Billing)))